import { connect } from 'react-redux';
import { AgeType } from '@eti/schema-types';
import { change as changeFormvalue } from 'redux-form';
import { useProperty } from '@eti/providers';
import { Inline, SelectBox, Text, vars } from '@etg/wings';
import { useEffect, useState } from 'react';
import { constants, price as priceUtils } from '@eti/utils';
import { Check } from '@phosphor-icons/react';
import { ConnectedSelectBox } from '@eti/form';
import NewSmallCardLayout from '../../common/components/small-card-layout/NewSmallCardLayout';
import { attachDropDownClickListener, sendSnowplowTracking } from '../utils/snowplow';
import { screenReaderOnly } from '../../common/utils/styles';
import ProductModal from '../../common/components/ProductModal';
import { flexibleTicketCtaGridLayoutStyles, infantNoteStyles, optionListStyles, selectBoxOptionLabelStyles, selectBoxStyles, textContentStyles } from '../utils/styles';
import ProductLayout from '../../common/main-layout/ProductLayout';
import { useMutationObserver } from '../../custom-hooks/useMutationObserver';
import { useDynamicIcon } from '../../common/utils/dynamic-icons/useDynamicIcon';
import { DYNAMIC_ICON } from '../../common/utils/dynamic-icons/icons';
import { FLEXIBLE_TICKET } from '../../products';
import { getTravelerNames } from '../../common/utils/travelerNames';
import { formatLabel, formatOptions, getMinPrice, getTotalPrice, handleSelectedOption, SELECTBOX_VALUE } from '../../common/utils/selectBoxOptions';
import { getSelected } from '../utils/selection';
import { ProductCardLayoutType } from '../../common/models/product';
import { getAgeTypeLabel } from '../../common/utils/ageTypes';
import { useProductData } from '../../common/hook/useProductData';
import { getOptions } from '../utils/flexibleTicketTransformer';
import { TRACKING_PROPERTIES } from '../../custom-hooks/useProductSnowplowTracking';
import { isEnabled as isModalEnabled } from '../../common/utils/modal';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const {
  name: PRODUCT_NAME,
  id: PRODUCT_ID
} = FLEXIBLE_TICKET;
const {
  Small: SMALL_CARD_LAYOUT,
  Main: MAIN_CARD_LAYOUT,
  PostBooking: POSTBOOKING_CARD_LAYOUT
} = ProductCardLayoutType;
const {
  MOUSE_FLOW_CLASS_NAMES
} = constants;
const sharedTexts = {
  adultLabel: 'Adult',
  allTravelersLabel: 'Product.FlexibleTicket.Select.All',
  childLabel: 'Child',
  infantNoteLabel: 'TravelerDetails.IncludedInfant.Text.Default',
  noTravelersLabel: 'Product.FlexibleTicket.Select.None',
  notSelectedLabel: 'Products.Price.Label.From',
  persuasionContent: 'flexibleTicketPersuasionContent',
  placeholder: 'Product.FlexibleTicket.Dropdown.Button.Text',
  placeholderSelectedPlural: 'Product.FlexibleTicket.Dropdown.Label.Selected.Plural',
  placeholderSelectedSingular: 'Product.FlexibleTicket.Dropdown.Label.Selected.Singular',
  readMoreContent: 'flexibleTicketReadMoreModalContent',
  selectboxLabel: 'Product.SelectOneValidation.Message',
  title: 'flexibleTicketHeaderTitle',
  totalLabel: 'Products.Price.Label.Total',
  defaultTravelerLabel: 'TravelerDescription.Header',
  dropDownCloseInstruction: 'ScreenReader.Product.CloseDropdown.Instruction.Text'
};
const mainCardTranslations = {
  ...sharedTexts,
  mainCardDescription: 'flexibleTicketAbstract',
  upSell: 'flexibleTicketUpSellText'
};
const translations = {
  [MAIN_CARD_LAYOUT]: mainCardTranslations,
  [POSTBOOKING_CARD_LAYOUT]: mainCardTranslations,
  [SMALL_CARD_LAYOUT]: {
    ...sharedTexts,
    abstractContent: 'flexibleTicketAbstractSmallCard',
    upSell: 'flexibleTicketUpSellTextSmallCard'
  }
};
export const SELECTBOX_TEST_ID = `${FLEXIBLE_TICKET.name}-select-box`;
const FlexibleTicket = _ref => {
  var _order$travelers, _getOptions;
  let {
    form,
    order,
    initialState,
    travelerNames,
    onModalConfirm,
    productPosition,
    layoutType = MAIN_CARD_LAYOUT,
    className
  } = _ref;
  const {
    p
  } = useProperty();
  const [isOpen, setIsOpen] = useState(false);
  const iconColor = vars.colors.signal.positive.foreground;
  const [icon] = useDynamicIcon(vars.colors.links.default, DYNAMIC_ICON.linkIcon);
  const hasInfants = order === null || order === void 0 || (_order$travelers = order.travelers) === null || _order$travelers === void 0 ? void 0 : _order$travelers.some(traveler => traveler.ageType === AgeType.Inf);
  const [selectedOptions, setSelectedOptions] = useState(initialState);
  const texts = translations[layoutType] || {};
  useEffect(() => {
    setSelectedOptions(initialState);
  }, [initialState]);
  const {
    productData,
    rootElement,
    translation,
    snowplowTracking: {
      sendClickEvent,
      sendAddToCartEvent,
      updateContext
    }
  } = useProductData({
    productId: PRODUCT_ID,
    order,
    productName: PRODUCT_NAME,
    productPosition,
    isSelected: null,
    form,
    className,
    texts
  });
  const {
    adultLabel,
    allTravelersLabel,
    childLabel,
    infantNoteLabel,
    noTravelersLabel,
    notSelectedLabel,
    persuasionContent,
    placeholder,
    placeholderSelectedPlural,
    placeholderSelectedSingular,
    selectboxLabel,
    title,
    totalLabel,
    defaultTravelerLabel,
    dropDownCloseInstruction,
    mainCardDescription
  } = translation;
  const isValidationEnabled = p('IbeClient.TravelerDetails.OptionalProductsRequireValidation.Enabled');
  const hasLabel = Boolean(isValidationEnabled && selectboxLabel);
  const options = (_getOptions = getOptions({
    id: PRODUCT_ID,
    order,
    travelerNames,
    defaultTravelerLabel
  })) === null || _getOptions === void 0 || (_getOptions = _getOptions.filter(_ref2 => {
    let {
      ageType
    } = _ref2;
    return ageType !== AgeType.Inf;
  })) === null || _getOptions === void 0 ? void 0 : _getOptions.map(_ref3 => {
    let {
      ageType,
      id,
      name,
      priceRaw
    } = _ref3;
    return {
      ageType,
      id,
      name: `${name}, ${getAgeTypeLabel(ageType, adultLabel, childLabel, '')}`,
      priceRaw
    };
  });
  const productPriceRaw = !(selectedOptions !== null && selectedOptions !== void 0 && selectedOptions.length) ? getMinPrice(options) : getTotalPrice(selectedOptions === null || selectedOptions === void 0 ? void 0 : selectedOptions.filter(option => !(option !== null && option !== void 0 && option.exclude)));
  const formattedOptions = formatOptions(options, allTravelersLabel, noTravelersLabel);
  const formatSelectedOption = opts => {
    const label = formatLabel({
      selectedOptions: opts,
      allTravelersLabel,
      defaultPlaceholder: placeholder,
      placeholderSelectedSingular,
      placeholderSelectedPlural,
      noTravelersLabel
    });
    return /*#__PURE__*/_jsx("p", {
      children: label
    });
  };
  const handleModalConfirmation = async () => {
    const confirmationValues = formattedOptions.filter(_ref4 => {
      let {
        value
      } = _ref4;
      return value !== 'NONE';
    });
    if ((confirmationValues === null || confirmationValues === void 0 ? void 0 : confirmationValues.length) > 0) {
      setSelectedOptions(confirmationValues);
      onModalConfirm(form, `${FLEXIBLE_TICKET.name}`, confirmationValues);
      await updateContext({
        quantity: formattedOptions.length - 2
      });
      sendAddToCartEvent({
        addToCart: true,
        property: TRACKING_PROPERTIES.VALIDATION_POP_UP
      });
    }
    setIsOpen(false);
  };
  useMutationObserver({
    targetElement: rootElement,
    selector: "[role='listbox']",
    onElementAdded: element => {
      element.setAttribute('aria-describedby', 'dropdown-close-instruction');
    }
  });
  const handleModalOpen = selectedOption => {
    const canOpenModal = isModalEnabled(order.persuasion) && selectedOption.find(option => option.value === SELECTBOX_VALUE.NONE);
    if (canOpenModal) {
      setIsOpen(true);
    }
  };
  useEffect(() => {
    const cleanUp = attachDropDownClickListener(rootElement, sendClickEvent);
    return () => {
      cleanUp();
    };
  }, [rootElement, sendClickEvent]);
  if (!productData) {
    return null;
  }
  const updatedProductData = {
    ...productData,
    price: priceUtils.formatPrice(productPriceRaw),
    priceLabel: !(selectedOptions !== null && selectedOptions !== void 0 && selectedOptions.length) ? notSelectedLabel : totalLabel,
    priceRaw: productPriceRaw !== null && productPriceRaw !== void 0 ? productPriceRaw : 0
  };
  const handleOptionClick = (event, option) => {
    sendSnowplowTracking(event, option, formattedOptions, options, updateContext, sendAddToCartEvent);
  };
  switch (layoutType) {
    case SMALL_CARD_LAYOUT:
      return /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsx(NewSmallCardLayout, {
          productData: updatedProductData,
          ref: rootElement,
          children: /*#__PURE__*/_jsx(NewSmallCardLayout.Cta, {
            customGrid: flexibleTicketCtaGridLayoutStyles(Boolean(selectboxLabel), layoutType === SMALL_CARD_LAYOUT),
            children: /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx("span", {
                "aria-hidden": true,
                className: screenReaderOnly,
                id: "dropdown-close-instruction",
                tabIndex: -1,
                children: dropDownCloseInstruction
              }), /*#__PURE__*/_jsx(ConnectedSelectBox, {
                className: selectBoxStyles,
                "data-testid": SELECTBOX_TEST_ID,
                formatSelectedOption: formatSelectedOption,
                isDisabled: !(formattedOptions !== null && formattedOptions !== void 0 && formattedOptions.length),
                isMultiple: true,
                label: hasLabel ? selectboxLabel : '',
                name: `${FLEXIBLE_TICKET.name}.value`,
                normalize: (newValue, prevVal) => {
                  if (!newValue) {
                    return selectedOptions;
                  }
                  const selectedOption = handleSelectedOption(formattedOptions, prevVal, newValue);
                  if (selectedOption !== null && selectedOption !== void 0 && selectedOption.length) {
                    setSelectedOptions(selectedOption);
                    handleModalOpen(selectedOption);
                    return selectedOption;
                  }
                  setSelectedOptions(null);
                  return null;
                },
                placeholder: placeholder,
                shouldMarkInputAsRequired: hasLabel,
                validationRuleName: FLEXIBLE_TICKET.name,
                children: /*#__PURE__*/_jsx("ul", {
                  className: optionListStyles,
                  children: formattedOptions === null || formattedOptions === void 0 ? void 0 : formattedOptions.map(option => /*#__PURE__*/_jsx(SelectBox.Option, {
                    isMultiple: true,
                    label: /*#__PURE__*/_jsxs(Inline, {
                      className: selectBoxOptionLabelStyles,
                      noWrap: true,
                      spacing: 4,
                      children: [/*#__PURE__*/_jsx("span", {
                        className: MOUSE_FLOW_CLASS_NAMES,
                        children: option.label
                      }), /*#__PURE__*/_jsx("span", {
                        "aria-hidden": true,
                        children: option.price
                      }), /*#__PURE__*/_jsx("span", {
                        className: screenReaderOnly,
                        children: priceUtils.replaceWithCurrencyName(option.price)
                      })]
                    }),
                    onClick: event => handleOptionClick(event, option),
                    value: option
                  }, option.value))
                })
              }), /*#__PURE__*/_jsx(ProductModal, {
                content: persuasionContent,
                isOpen: isOpen,
                onConfirm: handleModalConfirmation,
                onDecline: () => {
                  sendAddToCartEvent({
                    addToCart: false,
                    property: TRACKING_PROPERTIES.VALIDATION_POP_UP
                  });
                  setIsOpen(false);
                },
                productName: FLEXIBLE_TICKET.name,
                title: title
              })]
            })
          })
        })
      });
    case MAIN_CARD_LAYOUT:
    default:
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(ProductLayout, {
          productData: updatedProductData,
          ref: rootElement,
          children: [/*#__PURE__*/_jsx(ProductLayout.Body, {
            children: /*#__PURE__*/_jsxs(_Fragment, {
              children: [mainCardDescription && /*#__PURE__*/_jsx(Text, {
                asHtml: true,
                className: textContentStyles(icon),
                children: mainCardDescription
              }), hasInfants && infantNoteLabel && /*#__PURE__*/_jsxs(Inline, {
                className: infantNoteStyles,
                noWrap: true,
                spacing: 8,
                children: [/*#__PURE__*/_jsx(Check, {
                  color: iconColor,
                  "data-testid": "included-checks"
                }), /*#__PURE__*/_jsx(Text, {
                  children: infantNoteLabel
                })]
              })]
            })
          }), /*#__PURE__*/_jsx(ProductLayout.Cta, {
            customGrid: flexibleTicketCtaGridLayoutStyles(Boolean(selectboxLabel)),
            children: /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx("span", {
                "aria-hidden": true,
                className: screenReaderOnly,
                id: "dropdown-close-instruction",
                tabIndex: -1,
                children: dropDownCloseInstruction
              }), /*#__PURE__*/_jsx(ConnectedSelectBox, {
                className: selectBoxStyles,
                "data-testid": SELECTBOX_TEST_ID,
                formatSelectedOption: formatSelectedOption,
                isDisabled: !(formattedOptions !== null && formattedOptions !== void 0 && formattedOptions.length),
                isMultiple: true,
                label: hasLabel ? selectboxLabel : '',
                name: `${FLEXIBLE_TICKET.name}.value`,
                normalize: (newValue, prevVal) => {
                  if (!newValue) {
                    return selectedOptions;
                  }
                  const selectedOption = handleSelectedOption(formattedOptions, prevVal, newValue);
                  if (selectedOption !== null && selectedOption !== void 0 && selectedOption.length) {
                    setSelectedOptions(selectedOption);
                    handleModalOpen(selectedOption);
                    return selectedOption;
                  }
                  setSelectedOptions(null);
                  return null;
                },
                placeholder: placeholder,
                shouldMarkInputAsRequired: hasLabel,
                validationRuleName: FLEXIBLE_TICKET.name,
                children: /*#__PURE__*/_jsx("ul", {
                  className: optionListStyles,
                  children: formattedOptions === null || formattedOptions === void 0 ? void 0 : formattedOptions.map(option => /*#__PURE__*/_jsx(SelectBox.Option, {
                    isMultiple: true,
                    label: /*#__PURE__*/_jsxs(Inline, {
                      className: selectBoxOptionLabelStyles,
                      noWrap: true,
                      spacing: 4,
                      children: [/*#__PURE__*/_jsx("span", {
                        className: MOUSE_FLOW_CLASS_NAMES,
                        children: option.label
                      }), /*#__PURE__*/_jsx("span", {
                        "aria-hidden": true,
                        children: option.price
                      }), /*#__PURE__*/_jsx("span", {
                        className: screenReaderOnly,
                        children: priceUtils.replaceWithCurrencyName(option.price)
                      })]
                    }),
                    onClick: event => handleOptionClick(event, option),
                    value: option
                  }, option.value))
                })
              })]
            })
          })]
        }), /*#__PURE__*/_jsx(ProductModal, {
          content: persuasionContent,
          isOpen: isOpen,
          onConfirm: handleModalConfirmation,
          onDecline: () => {
            sendAddToCartEvent({
              addToCart: false,
              property: TRACKING_PROPERTIES.VALIDATION_POP_UP
            });
            setIsOpen(false);
          },
          productName: FLEXIBLE_TICKET.name,
          title: title
        })]
      });
  }
};
const handleModalConfirm = (dispatch, _ref5) => {
  let {
    form,
    productName,
    selectedOptions
  } = _ref5;
  dispatch(changeFormvalue(form, `${productName}.value`, selectedOptions));
};
const mapStateToProps = (state, _ref6) => {
  let {
    form,
    order
  } = _ref6;
  return {
    initialState: getSelected(FLEXIBLE_TICKET.name, form)(state),
    travelerNames: getTravelerNames(form, state, order.travelers)
  };
};
const mapDispatchToProps = dispatch => ({
  onModalConfirm: (form, productName, selectedOptions) => handleModalConfirm(dispatch, {
    form,
    productName,
    selectedOptions
  })
});
export default connect(mapStateToProps, mapDispatchToProps)(FlexibleTicket);
//# sourceMappingURL=FlexibleTicket.js.map